import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
  LayoutDashboard,
  UserPlus,
  Briefcase,
  Users,
  Bug,
  LogOut,
  ChevronDown,
  ChevronUp,
  Pin,
  User,
  FileText,
  Radio,
  ClipboardCheck,
  CalendarDays,
  ListChecks,
  ShieldCheck,
  Inbox,
  BookmarkCheck,
  UserCheck,
  FileBadge,
  UserX,
  Undo2,
  PauseCircle,
} from "lucide-react";

import { LogoutEmp, GetEmployeeName } from "../Services/SidebarService";
import NormalAlert from "../../../Alerts/NormalAlert";
import ConfirmationAlert from "../../../Alerts/ConfirmationAlert";
import { useNavigate } from "react-router-dom";

const Sidebar = ({ isPinned, setIsPinned, onHoverChange }) => {
  const navigate = useNavigate();
  const hoverTimer = useRef(null);
  const location = useLocation();

  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isPinned || isHovered;

  const [userName, setUserName] = useState(Cookies.get("username") || "User");
  const [employeeRole, setEmployeeRole] = useState(null);

  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
  const [isCandidateMenuOpen, setIsCandidateMenuOpen] = useState(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);

  const [confirmAlert, setConfirmAlert] = useState(null);
  const [alert, setAlert] = useState(null);

  /*  USER INFO  */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = Cookies.get("employeeToken");
        if (!token) return;

        const res = await GetEmployeeName(token);
        if (res?.data?.status === "Success") {
          setUserName(res.data.name);
          setEmployeeRole(res.data.employeeRole);
          Cookies.set("username", res.data.name);
          Cookies.set("employeeRole", res.data.employeeRole);
        }
      } catch {
        setUserName("User");
        setEmployeeRole(null);
      }
    };
    fetchUser();
  }, []);

  /*  AUTO OPEN MENUS */
  useEffect(() => {
    setIsAdminMenuOpen(
      ["/Dashboard/Add-Consultant", "/Dashboard/Add-Employee"].includes(
        location.pathname,
      ),
    );

    setIsJobMenuOpen(
      [
        "/Dashboard/PostJob",
        "/Dashboard/Draft-Jobs",
        "/Dashboard/Live-Jobs",
      ].includes(location.pathname),
    );

    setIsCandidateMenuOpen(
      [
        "/Dashboard/New-Applications",
        "/Dashboard/Shortlist-Candidates",
        "/Dashboard/Interview-Scheduled",
        "/Dashboard/Assessment-Candidates",
        "/Dashboard/Get-Selected-Candidates",
        "/Dashboard/Rejected-Candidates",
        "/Dashboard/Hold-Candidates",
        "/Dashboard/BGV-Candidates",
      ].includes(location.pathname),
    );

    setIsReportIssueOpen(location.pathname === "/Dashboard/Report-Issue");
  }, [location.pathname]);

  /*  LOGOUT  */
  const handleLogout = () => {
    setConfirmAlert({
      type: "warning",
      message: "Are you sure you want to logout?",
    });
  };

  const confirmLogout = async () => {
    try {
      await LogoutEmp(new Date());
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlert({
        type: "fail",
        message: safeMessage,
      });
    } finally {
      Cookies.remove("employeeToken");
      Cookies.remove("username");
      navigate("/");
    }
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <>
      <aside
        className="
          fixed top-0 left-0 h-screen z-50
          bg-gradient-to-b from-[#0d47a1] to-[#42a5f5]
          text-white flex flex-col
          transition-[width] duration-300 ease-in-out
        "
        style={{ width: isExpanded ? "250px" : "70px" }}
        onMouseEnter={() => {
          if (isPinned) return;

          hoverTimer.current = setTimeout(() => {
            setIsHovered(true);
            onHoverChange?.(true);
          }, 120);
        }}
        onMouseLeave={() => {
          if (isPinned) return;

          clearTimeout(hoverTimer.current);
          setIsHovered(false);
          onHoverChange?.(false);
        }}
      >
        {/*  PROFILE  */}
        <div className="h-[72px] flex items-center border-b border-white/20 px-4">
          {/* ICON COLUMN */}
          <div className="w-10 h-10 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center border border-white/70 font-semibold tracking-wide">
              {getInitials(userName)}
            </div>
          </div>
          {/* TEXT */}
          <div
            className={`
      ml-3 transition-all duration-300
      ${
        isExpanded
          ? "opacity-100 translate-x-0"
          : "opacity-0 -translate-x-2 pointer-events-none"
      }
    `}
          >
            <div className="text-sm font-semibold">Welcome</div>
            <div className="text-xs text-white/70">{userName}</div>
          </div>

          {/* PIN */}
          <button
            className={`ml-auto transition-all duration-300 ${isExpanded ? "opacity-100" : "opacity-0 pointer-events-none"} `}
            onClick={() => {
              const next = !isPinned;
              setIsPinned(next);
              Cookies.set("sidebarPinned", next ? "true" : "false");
            }}
          >
            <Pin size={18} />
          </button>
        </div>

        {/*  MENU  */}
        <nav
          className={`flex-1 py-2 space-y-2 overflow-y-auto sidebar-scroll ${
            isExpanded ? "px-2" : "px-0"
          }`}
        >
          <MenuItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            expanded={isExpanded}
            to="/Dashboard"
          />

          {/* {employeeRole === "Admin Employee" && (
            <MenuGroup
              icon={<UserPlus size={20} />}
              label="Admin"
              expanded={isExpanded}
              open={isAdminMenuOpen}
              toggle={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
            >
              <SubItem
                to="/Dashboard/Add-Consultant"
                label="Add Consultant"
                icon={<UserPlus size={16} />}
              />

              <SubItem
                to="/Dashboard/Add-Employee"
                label="Add Employee"
                icon={<User size={16} />}
              />

              <SubItem
                to="/Dashboard/Master-Data"
                label="Master Data"
                icon={<User size={16} />}
              />
            </MenuGroup>
          )} */}

          <MenuGroup
            icon={<Briefcase size={20} />}
            label="Job"
            expanded={isExpanded}
            open={isJobMenuOpen}
            toggle={() => setIsJobMenuOpen(!isJobMenuOpen)}
          >
            <SubItem
              to="/Dashboard/Post-Job"
              label="Post Job"
              icon={<Briefcase size={16} />}
            />

            <SubItem
              to="/Dashboard/Draft-Jobs"
              label="Draft Jobs"
              icon={<FileText size={16} />}
            />

            <SubItem
              to="/Dashboard/Live-Jobs"
              label="Live Jobs"
              icon={<Radio size={16} />}
            />
          </MenuGroup>

          <MenuGroup
            icon={<Users size={20} />}
            label="Candidate"
            expanded={isExpanded}
            open={isCandidateMenuOpen}
            toggle={() => setIsCandidateMenuOpen(!isCandidateMenuOpen)}
          >
            <SubItem
              to="/Dashboard/New-Applications"
              label="Incoming Applications"
              icon={<Inbox size={16} />}
            />

            <SubItem
              to="/Dashboard/Shortlist-Candidates"
              label="Shortlisted"
              icon={<BookmarkCheck size={16} />}
            />

            <SubItem
              to="/Dashboard/Interview-Scheduled"
              label="Interviews"
              icon={<CalendarDays size={16} />}
            />

            <SubItem
              to="/Dashboard/Assessment-Candidates"
              label="Assessments"
              icon={<ClipboardCheck size={16} />}
            />

            <SubItem
              to="/Dashboard/Get-Selected-Candidates"
              label="Selected Candidates"
              icon={<UserCheck size={16} />}
            />

            <SubItem
              to="/Dashboard/Offer-Released-Candidate"
              label="Offer-Released Candidates"
              icon={<FileBadge size={16} />}
            />

            <SubItem
              to="/Dashboard/Hired-Candidates"
              label="Hired Candidates"
              icon={<UserPlus size={16} />}
            />

            <SubItem
              to="/Dashboard/Rejected-Candidates"
              label="Rejected Candidates"
              icon={<UserX size={16} />}
            />

            <SubItem
              to="/Dashboard/Backout-Candidates"
              label="Backout Candidates"
              icon={<Undo2 size={16} />}
            />

            <SubItem
              to="/Dashboard/Hold-Candidates"
              label="Hold Candidates"
              icon={<PauseCircle size={16} />}
            />

            <SubItem
              to="/Dashboard/BGV-Candidates"
              label="Background-Verification Candidates"
              icon={<ShieldCheck size={16} />}
            />
          </MenuGroup>

          <MenuGroup
            icon={<Bug size={20} />}
            label="Report Issue"
            expanded={isExpanded}
            open={isReportIssueOpen}
            toggle={() => setIsReportIssueOpen(!isReportIssueOpen)}
          >
            <SubItem
              to="/Dashboard/Report-Issue"
              label="Report Issue"
              icon={<Bug size={16} />}
            />
          </MenuGroup>

          {employeeRole === "Admin Employee" && (
            <MenuItem
              icon={<ShieldCheck size={20} />}
              label="Go to Admin"
              expanded={isExpanded}
              to="/Admin/Dashboard"
            />
          )}
        </nav>

        {/*  LOGOUT  */}
        <div className="p-3 border-t border-white/20">
          <button
            onClick={handleLogout}
            className="w-full bg-white text-blue-700 rounded-md flex items-center justify-center gap-2 py-2"
          >
            <LogOut size={18} />
            <span
              className={`transition-all duration-200 ${isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"}`}
            >
              Logout
            </span>
          </button>
        </div>
      </aside>

      {confirmAlert && (
        <ConfirmationAlert
          {...confirmAlert}
          onConfirm={confirmLogout}
          onCancel={() => setConfirmAlert(null)}
        />
      )}

      {alert && <NormalAlert {...alert} onClose={() => setAlert(null)} />}
    </>
  );
};

/*COMPONENTS */

const MenuItem = ({ icon, label, expanded, to }) => (
  <Link
    to={to}
    className={`
      flex items-center rounded-md transition-all duration-200
     ${expanded ? "px-4 gap-3 justify-start" : "px-4 justify-center"}
      hover:bg-white hover:text-blue-800
    `}
    style={{ height: "56px" }}
  >
    <div
      className={`w-10 h-10 flex items-center justify-center shrink-0 ${
        expanded ? "" : "mx-auto"
      }`}
    >
      {icon}
    </div>

    <span
      className={`whitespace-nowrap transition-all duration-200 ${
        expanded
          ? "opacity-100 translate-x-0"
          : "opacity-0 -translate-x-2 w-0 overflow-hidden"
      }`}
    >
      {label}
    </span>
  </Link>
);

const MenuGroup = ({ icon, label, expanded, open, toggle, children }) => (
  <>
    <button
      onClick={toggle}
      className={`w-full flex items-center rounded-md transition-all duration-200 ${expanded ? "px-4 gap-3 justify-start" : "px-4 justify-center"}
 hover:bg-white hover:text-blue-800`}
      style={{ height: "46px" }}
    >
      <div
        className={`w-10 h-10 flex items-center justify-center shrink-0 ${
          expanded ? "" : "mx-auto"
        }`}
      >
        {icon}
      </div>

      <span
        className={`flex-1 text-left whitespace-nowrap transition-all duration-200 ${
          expanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
        }`}
      >
        {label}
      </span>

      {expanded && (open ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
    </button>

    {open && expanded && <div className="mt-1 space-y-1">{children}</div>}
  </>
);

const SubItem = ({ to, label, icon }) => (
  <Link
    to={to}
    className="
      flex items-center gap-3
      h-9
      pl-[48px]
      pr-3
      text-sm text-white/85
      rounded-md
      hover:bg-white/10 hover:text-white
      transition-colors
    "
  >
    <span className="w-5 h-5 flex items-center justify-center shrink-0">
      {icon}
    </span>
    <span className="truncate">{label}</span>
  </Link>
);

export default Sidebar;
